// Implements: F-UIX-018, F-USG-005
/**
 * The key numbers of the consumption screen (the redesign mockup's `.tiles`): the spend of the
 * period first and large, then what was delivered, failed and cancelled, for ONE currency (money is
 * never added across currencies). Pure presentation; money is formatted here, in the backoffice only
 * (OD-12). The mockup also shows a cap meter, a projection and a count of created mockups; the
 * report serves none of them, so they are not drawn.
 */

import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { moneyText } from "../../../shared/ui/moneyText";
import type { FiguresView } from "../../../types/reports";
import styles from "./ConsumptionTiles.module.css";

export interface ConsumptionTilesProps {
  readonly figures: FiguresView;
  /** The report's range, in words, for the line under the spend. */
  readonly range: string;
}

export function ConsumptionTiles({ figures, range }: ConsumptionTilesProps): ReactElement {
  const { t } = useTranslation();
  return (
    <ul className={styles.tiles} aria-label={t("backoffice.consumption.tiles.label")}>
      <li className={styles.tile}>
        <span className={styles.label}>{t("backoffice.consumption.tile.spend")}</span>
        <strong className={styles.value}>{moneyText(figures.cost_micros, figures.currency)}</strong>
        {range === "" ? null : <span className={styles.meta}>{range}</span>}
      </li>
      <li className={styles.tile}>
        <span className={styles.label}>{t("backoffice.consumption.tile.delivered")}</span>
        <strong className={styles.value}>{figures.delivered}</strong>
      </li>
      <li className={styles.tile}>
        <span className={styles.label}>{t("backoffice.consumption.tile.failed")}</span>
        <strong className={styles.value}>{figures.failed}</strong>
      </li>
      <li className={styles.tile}>
        <span className={styles.label}>{t("backoffice.consumption.tile.cancelled")}</span>
        <strong className={styles.value}>{figures.cancelled}</strong>
      </li>
    </ul>
  );
}
